/**
 * QuickChatsTab — lists conversations from the Floating CFO Buck "Quick Chat"
 * bubble (the FAB in the bottom-right corner). Renders inside CFO Buck →
 * History tab as the 3rd sub-tab. Clicking a row routes to /chat-history?conv=…
 * (the dedicated full-page viewer) so the user can read & continue the chat.
 *
 * Note: AI Studio chats are a SEPARATE system (stored in `ai_hub_threads`)
 * and are managed inside AI Studio's own left-rail Conversations list. We
 * intentionally don't mix them here because they have different metadata,
 * models, and saving conventions.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../fetchInterceptor';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { MessageCircle, ArrowRight, Trash2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (new Date() - new Date(iso)) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function QuickChatsTab() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchConversations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API}/api/quick-chat/conversations`);
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations || data || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  const handleDelete = async (e, convId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation? This cannot be undone.')) return;
    try {
      const res = await apiFetch(`${API}/api/quick-chat/conversations/${convId}`, { method: 'DELETE' });
      if (res.ok) {
        setConversations((prev) => prev.filter((c) => c.conversation_id !== convId));
        toast.success('Conversation deleted');
      } else {
        toast.error('Delete failed');
      }
    } catch { toast.error('Delete failed'); }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="quick-chats-loading">
        {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-20" />)}
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <Card className="border-dashed" data-testid="quick-chats-empty">
        <CardContent className="py-12 text-center">
          <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm text-muted-foreground">No quick chats yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Click the floating CB bubble (bottom-right) to start a quick chat with CFO Buck.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2" data-testid="quick-chats-list">
      <p className="text-xs text-muted-foreground">
        {conversations.length} quick chat{conversations.length === 1 ? '' : 's'} from the floating CFO Buck bubble. Click any to open in full view.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {conversations.map((c) => (
          <button
            type="button"
            key={c.conversation_id}
            onClick={() => navigate(`/chat-history?conv=${c.conversation_id}`)}
            className="group flex items-center gap-3 rounded-lg border bg-card hover:bg-muted/40 hover:border-emerald-500/40 p-3 transition-all text-left"
            data-testid={`quick-chat-${c.conversation_id}`}
          >
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{c.title || 'Untitled conversation'}</p>
              <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {timeAgo(c.updated_at || c.created_at)}
                {c.message_count != null && (
                  <span className="ml-1">· {c.message_count} message{c.message_count === 1 ? '' : 's'}</span>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={(e) => handleDelete(e, c.conversation_id)}
              className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100 transition-all"
              title="Delete conversation"
              data-testid={`quick-chat-delete-${c.conversation_id}`}
            >
              <Trash2 className="w-3.5 h-3.5 text-red-500" />
            </button>
            <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}
